import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';

export function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');

    if (code) {
      let isSubscribed = true;

      const performExchange = async () => {
        try {
          const data = await authService.googleExchange(code);
          if (!isSubscribed) return;

          const { token, user } = data;

          // Save data locally just like standard login does
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(user));
          
          // Update context
          updateUser(user);

          // Give React a frame to process state updates before navigating
          requestAnimationFrame(() => {
            if (user.isAdmin) {
              navigate('/admin', { replace: true });
            } else {
              navigate('/dashboard', { replace: true });
            }
          });
        } catch (err: any) {
          if (!isSubscribed) return;
          console.error('OAuth exchange error:', err);
          setError(err.message || 'Google authentication failed.');
          setTimeout(() => navigate('/login'), 3000);
        }
      };

      performExchange();

      return () => {
        isSubscribed = false;
      };
    } else {
      setError('Invalid OAuth callback. Missing exchange code.');
      setTimeout(() => navigate('/login'), 3000);
    }
  }, [searchParams, navigate, updateUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white">
        <div className="p-6 bg-red-500/10 border border-red-500/20 text-red-500 rounded-xl">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A0A0A] text-white">
      <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="mt-4 text-gray-400 font-medium">Authenticating securely with Google...</p>
    </div>
  );
}

export default OAuthCallback;
